import {
  fitzpatrick_scale_modifiers as fitzpatrickScaleModifiers,
  lib,
  ordered,
} from 'emojilib';
import _ from 'lodash';
import fuzzySearch, {
  FuzzySearchIndex,
  FuzzySearchThreshold,
} from './search/fuzzySearch';

export interface Emoji {
  name: string;
  /**
   * Ensure "tree" matches "evergreen_tree" with strict threshold.
   */
  nameKeywords: string[];
  char: string;
  category: string;
  keywords: string[];
  fitzpatrickScale: boolean;
}

export const getEmojiItemByName = (name: string): Emoji | undefined => {
  const emoji = lib[name];
  return (
    emoji && {
      name,
      nameKeywords: name.split('_'),
      char: emoji.char,
      category: emoji.category,
      keywords: emoji.keywords,
      fitzpatrickScale: emoji.fitzpatrick_scale,
    }
  );
};

// Ignore emojis that are not widely supported in order to make the picker looks good.
// We don't want to enumerate all of them, but just ones on the first page.
const unsupportedEmojiNames = new Set(['relaxed', 'frowning_face']);
export const emojiList = ordered
  .map(getEmojiItemByName)
  .filter(
    (emoji): emoji is Emoji =>
      !!emoji && !unsupportedEmojiNames.has(emoji.name),
  );

const index = new FuzzySearchIndex(emojiList, [
  'name',
  'nameKeywords',
  'keywords',
]);

export const searchEmoji = (query: string, limit?: number) => {
  const search = (threshold: FuzzySearchThreshold) =>
    fuzzySearch(query, index, {
      threshold,
      weights: { name: 1, nameKeywords: 0.1, keywords: 0.1 },
    });

  // Prefer strict results to avoid false matches like:
  // - "tree" -> "three"
  // - "yes"  -> "eyes".
  // Fallback to loose results to tolerate typos.
  const strictResults = search(FuzzySearchThreshold.Strict);
  const results = strictResults.length
    ? strictResults
    : search(FuzzySearchThreshold.Loose);
  const slicedResults = limit ? results.slice(0, limit) : results;
  return slicedResults
    .map(({ item: object }) => object)
    .sort((a, b) => {
      const aPrefix = a.name.startsWith(query) ? 1 : 0;
      const bPrefix = b.name.startsWith(query) ? 1 : 0;
      return aPrefix === bPrefix
        ? a.name.localeCompare(b.name)
        : bPrefix - aPrefix;
    });
};

export const EMOJI_CATEGORIES = [
  'people',
  'animals_and_nature',
  'food_and_drink',
  'travel_and_places',
  'activity',
  'objects',
  'symbols',
  'flags',
];

const zeroWidthJoiner = String.fromCodePoint(0x200d);
const presentationSelectors = String.fromCodePoint(0xfe0f);
const zwjRegExp = new RegExp(
  `${presentationSelectors}?${zeroWidthJoiner}`,
  'g',
);

/**
 * Apply a skin tone modifier to emoji
 *
 * Generally, to apply a skin tone modifier to an emoji:
 * 1. If the emoji is a an emoji character, we can simply append the modifier:
 *    👩 -> 👩 + MODIFIER -> 👩🏽
 *
 * 2. If the emoji is a zwj sequence, we need to insert the modifier before ZWJ:
 *    👩‍💻 is a combination of 👩 + ZWJ + 💻.
 *    👩🏽‍💻 is a combination of 👩 + MODIFIER + ZWJ + 💻.
 *
 * 3. Some characters and sequences in emojilib contain presentation selector that
 * is used to request an emoji presentation (versus text presentation) for an
 * emoji character. For theses sequences we need to remove it
 * (see 2.4.1 in https://unicode.org/reports/tr51/):
 *    🖐️ is a combination of 🖐 (unqualified) + PRESENTATION_SELECTOR
 *    🖐🏽 is a combination of 🖐 + MODIFIER
 *
 *    🕵️‍♀️ is a combination of 🕵️ + PRESENTATION_SELECTOR + ZWJ + ♀️
 *    🕵🏽‍♀️ is a combination of 🕵️ + MODIFIER + ZWJ + ♀️
 *
 * As recommended by 2.7.1, this function always returns fully-qualified sequences.
 *
 * @see
 * 1. ZWJ: https://en.wikipedia.org/wiki/Zero-width_joiner
 * 2. Presentation selector: https://unicode.org/reports/tr51/#def_emoji_presentation_selector
 */
export const applySkinToneToEmoji = (
  { char, fitzpatrickScale }: Emoji,
  modifier: string | null,
) => {
  if (modifier == null || !fitzpatrickScale) return char;
  if (zwjRegExp.test(char)) {
    return char.replace(zwjRegExp, `${modifier}${zeroWidthJoiner}`);
  }
  if (char[char.length - 1] === presentationSelectors) {
    return char.slice(0, char.length - 1) + modifier;
  }
  return char + modifier;
};

const modifierAndSelectorRegExp = new RegExp(
  `[${fitzpatrickScaleModifiers.join(
    '',
  )}${presentationSelectors}${zeroWidthJoiner}]`,
  'g',
);

// Normalize an emoji by removing all modifiers to create a simplest
// representation (maybe unqualified).
const removeAllModifiers = (char: string) =>
  char.replace(modifierAndSelectorRegExp, '');
const emojiItemLookup = _.keyBy(emojiList, (emoji) =>
  removeAllModifiers(emoji.char),
);

/**
 * Get the emoji item by a character/sequence.
 *
 * It worth noticing that for inputs that are skin tone modified, we don't
 * want to remove the skin tone as that will require an external lookup table
 * (ex https://github.com/iamcal/emoji-data) in order to get a fully-qualified
 * result.
 * Instead, we create a lookup table whose keys are "normalized" by removing
 * all modifiers, and we'll do the same thing to inputs so their skin tone
 * modifiers will be removed as well.
 */
export const getEmojiItemByChar = (char: string) =>
  emojiItemLookup[removeAllModifiers(char)];

export const skinToneModifiers = fitzpatrickScaleModifiers;

export const DEFAULT_EMOJI_NAMES = [
  'smile',
  'clap',
  'rocket',
  '+1',
  '-1',
  'thinking',
];

export const isEmojiChar = (string: string) => {
  if (typeof string !== 'string') return false;
  return !!getEmojiItemByChar(string);
};
